import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Text,
  Group,
  Badge,
  Button,
  Modal,
  TextInput,
  Textarea,
  Stack,
  Alert,
  Container,
  TextInput as MantineTextInput,
  Table,
  ActionIcon,
  Select,
  ScrollArea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  IconPlus,
  IconAlertCircle,
  IconEye,
  IconPencil,
  IconTrash,
  IconCaretDownFilled,
  IconCaretUpFilled,
} from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { servicesApi, deploymentsApi } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { EmptyState } from "../components/EmptyState";
import { PageTransition } from "../components/PageTransition";
import { useAuth } from "../contexts/AuthContext";
import { showSuccess } from "../lib/notifications";

interface CreateServiceForm {
  name: string;
  description: string;
  repoName: string;
}

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
    },
  }),
};

export function ServicesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [createRepoDropdownOpen, setCreateRepoDropdownOpen] = useState(false);
  const [editRepoDropdownOpen, setEditRepoDropdownOpen] = useState(false);

  const canCreateService = ["SUPERUSER", "ADMIN", "DEVOPS"].includes(
    user?.role || ""
  );

  const form = useForm<CreateServiceForm>({
    initialValues: {
      name: "",
      description: "",
      repoName: "",
    },
    validate: {
      name: (value) => {
        if (!value) return "Name is required";
        if (!/^[a-z0-9-]+$/.test(value))
          return "Name can only contain lowercase letters, numbers, and hyphens";
        return null;
      },
      description: (value) => (value ? null : "Description is required"),
      repoName: (value) => {
        if (!value) return "Repository name is required";
        if (!/^[a-zA-Z0-9_.-]+$/.test(value))
          return "Repository name can only contain letters, numbers, underscores, dots, and hyphens";
        return null;
      },
    },
  });

  const { data: services, isLoading } = useQuery({
    queryKey: ["services"],
    queryFn: async () => {
      try {
        const response = await servicesApi.list();
        return response.data;
      } catch (error) {
        throw error;
      }
    },
  });

  const { data: repos = [], isLoading: reposLoading } = useQuery({
    queryKey: ["github-repositories"],
    queryFn: async () => {
      const res = await deploymentsApi.getRepositories();
      return res.data.repositories;
    },
  });

  const createService = useMutation({
    mutationFn: async (data: CreateServiceForm) => {
      try {
        const response = await servicesApi.create(data);
        return response.data;
      } catch (error) {
        throw error;
      }
    },
    onSuccess: (newService) => {
      // Update the services cache with the new service
      queryClient.setQueryData(["services"], (oldServices: any[] = []) => {
        return [...oldServices, newService];
      });
      setCreateModalOpen(false);
      form.reset();
      setError("");
      showSuccess("Service created successfully");
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || "Failed to create service");
    },
  });

  const updateService = useMutation({
    mutationFn: async (data: { description?: string; repoName?: string }) => {
      if (!selectedService) return;
      const response = await servicesApi.update(selectedService.id, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["services"]);
      setEditModalOpen(false);
      setSelectedService(null);
      form.reset();
      showSuccess("Service updated successfully");
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || "Failed to update service");
    },
  });

  const deleteService = useMutation({
    mutationFn: async () => {
      if (!selectedService) return;
      await servicesApi.delete(selectedService.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["services"]);
      setDeleteModalOpen(false);
      setSelectedService(null);
      showSuccess("Service deleted successfully");
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || "Failed to delete service");
    },
  });

  const handleCreateService = (values: CreateServiceForm) => {
    setError("");
    createService.mutate(values);
  };

  const handleEditService = (values: CreateServiceForm) => {
    setError("");
    updateService.mutate(values);
  };

  const handleDeleteService = () => {
    setError("");
    deleteService.mutate();
  };

  const handleOpenCreateModal = () => {
    setCreateModalOpen(true);
  };

  // Filter services based on search
  const filteredServices = services?.filter((service: any) =>
    search
      ? service.name.toLowerCase().includes(search.toLowerCase()) ||
        service.description?.toLowerCase().includes(search.toLowerCase())
      : true
  );

  if (isLoading) {
    return <LoadingOverlay message="Loading..." />;
  }

  return (
    <Container fluid p={0} pos="relative">
      <PageTransition>
        <PageHeader
          title="Services"
          description="Manage your services and their environments"
        />

        {services?.length === 0 ? (
          <EmptyState
            title="No services found"
            description="Create your first service to get started with environment and variable management."
            icon="add"
            action={
              canCreateService
                ? {
                    label: "Create Service",
                    onClick: handleOpenCreateModal,
                    icon: <IconPlus size={16} />,
                  }
                : undefined
            }
          />
        ) : (
          <Card mt="md" shadow="sm" radius="md" withBorder>
            <Group p="md" position="apart" align="center">
              <MantineTextInput
                placeholder="Search services..."
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                sx={(theme) => ({
                  width: 300,
                  [theme.fn.smallerThan('sm')]: { width: '100%' },
                })}
              />
              {canCreateService && (
                <Button
                  leftIcon={<IconPlus size={16} />}
                  onClick={handleOpenCreateModal}
                >
                  Create Service
                </Button>
              )}
            </Group>
            <ScrollArea>
              <Table>
                <thead>
                  <tr>
                    <th>Service Name</th>
                    <th>Description</th>
                    <th>Environments</th>
                    <th>Variables</th>
                    <th>GitHub Repository</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence>
                    {filteredServices?.map((service: any, index: number) => (
                      <motion.tr
                        key={service.id}
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        custom={index}
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/services/${service.id}`)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor =
                            "rgba(255, 54, 33, 0.06)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        <td>
                          <Text weight={500}>{service.name}</Text>
                        </td>
                        <td>
                          <Text size="sm" color="dimmed" lineClamp={2}>
                            {service.description || "No description"}
                          </Text>
                        </td>
                        <td>
                          <Badge color="thanos" variant="light">
                            {service._count.environments}
                          </Badge>
                        </td>
                        <td>
                          <Badge color="green" variant="light">
                            {service._count.variables}
                          </Badge>
                        </td>
                        <td>
                          <Text size="sm" color="dimmed">
                            {service.githubRepo || "Not specified"}
                          </Text>
                        </td>
                        <td>
                          <Group spacing={4}>
                            {canCreateService && (
                              <>
                                <ActionIcon
                                  variant="subtle"
                                  color="orange"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedService(service);
                                    form.setValues({
                                      name: service.name,
                                      description: service.description || "",
                                      repoName: service.githubRepo || "",
                                    });
                                    setEditModalOpen(true);
                                  }}
                                >
                                  <IconPencil size={16} />
                                </ActionIcon>
                                <ActionIcon
                                  variant="subtle"
                                  color="red"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedService(service);
                                    setDeleteModalOpen(true);
                                  }}
                                >
                                  <IconTrash size={16} />
                                </ActionIcon>
                              </>
                            )}
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/services/${service.id}`);
                              }}
                            >
                              <IconEye size={16} />
                            </ActionIcon>
                          </Group>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </Table>
            </ScrollArea>
          </Card>
        )}
      </PageTransition>

      <Modal
        opened={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setError("");
          form.reset();
        }}
        title="Create Service"
        size="md"
        centered
        transitionProps={{ duration: 200 }}
        overlayProps={{
          opacity: 0.55,
          blur: 3,
          color: "#000",
        }}
        keepMounted={false}
      >
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            mb="md"
            title="Error"
          >
            {error}
          </Alert>
        )}

        <form onSubmit={form.onSubmit(handleCreateService)}>
          <Stack>
            <TextInput
              label="Name"
              placeholder="my-service"
              description="Only lowercase letters, numbers, and hyphens"
              required
              {...form.getInputProps("name")}
            />
            <Textarea
              label="Description"
              placeholder="Brief description of the service"
              required
              {...form.getInputProps("description")}
            />
            <Select
              label="Repository Name"
              placeholder="Select repository"
              description="Repository name within Small-Group-Org organization"
              required
              data={repos.map((repo) => ({ value: repo, label: repo }))}
              searchable
              nothingFound="No repositories found"
              disabled={reposLoading}
              rightSection={
                createRepoDropdownOpen ? (
                  <IconCaretUpFilled size={16} />
                ) : (
                  <IconCaretDownFilled size={16} />
                )
              }
              onDropdownOpen={() => setCreateRepoDropdownOpen(true)}
              onDropdownClose={() => setCreateRepoDropdownOpen(false)}
              {...form.getInputProps("repoName")}
            />
            <Button type="submit" loading={createService.isLoading} fullWidth>
              Create Service
            </Button>
          </Stack>
        </form>
      </Modal>

      {/* Edit Service Modal */}
      <Modal
        opened={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedService(null);
          setError("");
          form.reset();
        }}
        title="Edit Service"
        size="md"
        centered
        transitionProps={{ duration: 200 }}
        overlayProps={{
          opacity: 0.55,
          blur: 3,
          color: "#000",
        }}
        keepMounted={false}
      >
        {error && (
          <Alert
            color="red"
            title="Error"
            icon={<IconAlertCircle size={16} />}
            mb="md"
          >
            {error}
          </Alert>
        )}

        <form onSubmit={form.onSubmit(handleEditService)}>
          <Stack>
            <TextInput
              label="Name"
              placeholder="my-service"
              description="Only lowercase letters, numbers, and hyphens"
              required
              disabled
              {...form.getInputProps("name")}
            />
            <Textarea
              label="Description"
              placeholder="Brief description of the service"
              {...form.getInputProps("description")}
            />
            <Select
              label="Repository Name"
              placeholder="Select repository"
              description="Repository name within Small-Group-Org organization"
              required
              data={repos.map((repo) => ({ value: repo, label: repo }))}
              searchable
              nothingFound="No repositories found"
              disabled={reposLoading}
              rightSection={
                editRepoDropdownOpen ? (
                  <IconCaretUpFilled size={16} />
                ) : (
                  <IconCaretDownFilled size={16} />
                )
              }
              onDropdownOpen={() => setEditRepoDropdownOpen(true)}
              onDropdownClose={() => setEditRepoDropdownOpen(false)}
              {...form.getInputProps("repoName")}
            />
            <Button type="submit" loading={updateService.isLoading} fullWidth>
              Update Service
            </Button>
          </Stack>
        </form>
      </Modal>

      {/* Delete Service Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedService(null);
          setError("");
        }}
        title="Delete Service"
        size="md"
        centered
        transitionProps={{ duration: 200 }}
        overlayProps={{
          opacity: 0.55,
          blur: 3,
          color: "#000",
        }}
        keepMounted={false}
      >
        {error && (
          <Alert
            color="red"
            title="Error"
            icon={<IconAlertCircle size={16} />}
            mb="md"
          >
            {error}
          </Alert>
        )}

        <Stack>
          <Text>
            Are you sure you want to delete the service "{selectedService?.name}
            "? This action cannot be undone and will also delete all associated
            environments and variables.
          </Text>
          <Group position="right">
            <Button
              variant="default"
              onClick={() => {
                setDeleteModalOpen(false);
                setSelectedService(null);
              }}
            >
              Cancel
            </Button>
            <Button
              color="red"
              loading={deleteService.isLoading}
              onClick={handleDeleteService}
            >
              Delete Service
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
